import React, { useState, useEffect } from 'react';
import UserProfile from './settings/UserProfile';
import Appearance from './settings/Appearance';
import UserAdministration from './settings/UserAdministration';
import ApiKeys from './settings/ApiKeys';
import GlobalApiKeys from './settings/GlobalApiKeys';
import ModelDefaultsSettings from './settings/ModelDefaults';
import VoiceAssistant from './settings/VoiceAssistant';
import ProjectOverview from './settings/ProjectOverview';
import ProjectIntegrations from './settings/ProjectIntegrations';
import ProjectUsers from './settings/ProjectUsers';
import { useProject } from '../context/ProjectContext';
import axios from 'axios';
import { API_URL } from '../config';


type SettingsSection = 'user' | 'integrations' | 'appearance' | 'apikeys' | 'models' | 'voice' | 'users' | 'global-apikeys' | string;
type SettingsCategory = 'settings' | 'administration' | 'projects';

interface Project {
  id: number;
  name: string;
  description?: string;
  github_repo?: string;
  jira_project_key?: string;
  user_role?: string;
  is_default: boolean;
  is_active: boolean;
}

const SettingsNew: React.FC = () => {
  const { projects: contextProjects, refreshProjects } = useProject();
  const [activeSection, setActiveSection] = useState<SettingsSection>('user');
  const [expandedProjects, setExpandedProjects] = useState<Set<number>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    if (contextProjects) {
      setProjects(contextProjects);
    }
  }, [contextProjects]);

  const getCategory = (section: SettingsSection): SettingsCategory => {
    if (section === 'users' || section === 'global-apikeys') return 'administration';
    if (section.startsWith('project-')) return 'projects';
    return 'settings';
  };

  const activeCategory = getCategory(activeSection);

  const toggleProject = (projectId: number) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
    }
    setExpandedProjects(newExpanded);
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/projects/`, formData);
      await refreshProjects();
      setShowCreateModal(false);
      setFormData({ name: '', description: '' });
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to create project');
    }
  };

  // Get current user to check if admin
  const userStr = localStorage.getItem('user');
  const currentUser = userStr ? JSON.parse(userStr) : null;
  const isAdmin = currentUser?.role === 'admin';

  return (
    <div className="flex h-full">
      {/* Left Sidebar Menu */}
      <div className="w-64 bg-white dark:bg-gray-700 border-r border-gray-200 dark:border-gray-600">
        <div className="p-6">
          <h2 className="text-xl font-sans font-bold normal-case text-gray-800 dark:text-white mb-6">Settings</h2>

          {/* Settings Category */}
          <div className="mb-6">
            <h3 className="text-xs font-bold text-gray-600 dark:text-gray-300 normal-case font-sans mb-2">
              Settings
            </h3>
            <nav className="space-y-1">
              <button
                onClick={() => setActiveSection('user')}
                className={`w-full text-left px-3 py-2 rounded transition font-sans text-sm ${
                  activeSection === 'user'
                    ? 'bg-blue-500 text-white font-semibold'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
              >
                User Profile
              </button>
              <button
                onClick={() => setActiveSection('appearance')}
                className={`w-full text-left px-3 py-2 rounded transition font-sans text-sm ${
                  activeSection === 'appearance'
                    ? 'bg-blue-500 text-white font-semibold'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
              >
                Appearance
              </button>
              <button
                onClick={() => setActiveSection('apikeys')}
                className={`w-full text-left px-3 py-2 rounded transition font-sans text-sm ${
                  activeSection === 'apikeys'
                    ? 'bg-blue-500 text-white font-semibold'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
              >
                API Keys
              </button>
              <button
                onClick={() => setActiveSection('models')}
                className={`w-full text-left px-3 py-2 rounded transition font-sans text-sm ${
                  activeSection === 'models'
                    ? 'bg-blue-500 text-white font-semibold'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
              >
                Default Models
              </button>
              <button
                onClick={() => setActiveSection('voice')}
                className={`w-full text-left px-3 py-2 rounded transition font-sans text-sm ${
                  activeSection === 'voice'
                    ? 'bg-blue-500 text-white font-semibold'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
              >
                Voice Assistant
              </button>
            </nav>
          </div>

          {/* Projects Category */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-bold text-gray-600 dark:text-gray-300 normal-case font-sans">
                Projects
              </h3>
              <button
                onClick={() => setShowCreateModal(true)}
                className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 text-xl font-bold"
                title="Create new project"
              >
                +
              </button>
            </div>
            <nav className="space-y-1">
              {projects.map(project => (
                <div key={project.id}>
                  {/* Project Header */}
                  <button
                    onClick={() => toggleProject(project.id)}
                    className={`w-full text-left px-3 py-2 rounded transition font-sans text-sm flex items-center justify-between ${
                      activeSection.startsWith(`project-${project.id}`)
                        ? 'bg-blue-500 text-white font-semibold'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span>{expandedProjects.has(project.id) ? '▼' : '▶'}</span>
                      <span className="truncate">{project.name}</span>
                      {project.is_default && (
                        <span className="text-xs">🟢</span>
                      )}
                    </span>
                  </button>

                  {/* Project Submenu */}
                  {expandedProjects.has(project.id) && (
                    <div className="ml-4 mt-1 space-y-1">
                      <button
                        onClick={() => setActiveSection(`project-${project.id}-overview`)}
                        className={`w-full text-left px-3 py-1.5 rounded transition font-sans text-xs ${
                          activeSection === `project-${project.id}-overview`
                            ? 'bg-blue-400 text-white font-semibold'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600'
                        }`}
                      >
                        Overview
                      </button>
                      <button
                        onClick={() => setActiveSection(`project-${project.id}-integrations`)}
                        className={`w-full text-left px-3 py-1.5 rounded transition font-sans text-xs ${
                          activeSection === `project-${project.id}-integrations`
                            ? 'bg-blue-400 text-white font-semibold'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600'
                        }`}
                      >
                        Integrations
                      </button>
                      <button
                        onClick={() => setActiveSection(`project-${project.id}-users`)}
                        className={`w-full text-left px-3 py-1.5 rounded transition font-sans text-xs ${
                          activeSection === `project-${project.id}-users`
                            ? 'bg-blue-400 text-white font-semibold'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600'
                        }`}
                      >
                        Users
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>

          {/* Administration Category (Admin only) */}
          {isAdmin && (
            <div>
              <h3 className="text-xs font-bold text-gray-600 dark:text-gray-300 normal-case font-sans mb-2">
                Administration
              </h3>
              <nav className="space-y-1">
                <button
                  onClick={() => setActiveSection('users')}
                  className={`w-full text-left px-3 py-2 rounded transition font-sans text-sm ${
                    activeSection === 'users'
                      ? 'bg-blue-500 text-white font-semibold'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  Users
                </button>
                <button
                  onClick={() => setActiveSection('global-apikeys')}
                  className={`w-full text-left px-3 py-2 rounded transition font-sans text-sm ${
                    activeSection === 'global-apikeys'
                      ? 'bg-blue-500 text-white font-semibold'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  Global API Keys
                </button>
              </nav>
            </div>
          )}
        </div>
      </div>

      {/* Right Content Area */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-4xl">
          {activeSection === 'user' && <UserProfile />}
          {activeSection === 'appearance' && <Appearance />}
          {activeSection === 'apikeys' && <ApiKeys />}
          {activeSection === 'models' && <ModelDefaultsSettings />}
          {activeSection === 'voice' && <VoiceAssistant />}
          {activeSection === 'users' && isAdmin && <UserAdministration />}
          {activeSection === 'global-apikeys' && isAdmin && <GlobalApiKeys />}

          {/* Project-specific sections */}
          {activeSection.startsWith('project-') && (() => {
            const match = activeSection.match(/^project-(\d+)-(.+)$/);
            if (!match) return null;

            const projectId = parseInt(match[1]);
            const subsection = match[2];
            const project = projects.find(p => p.id === projectId);

            if (!project) return null;

            if (subsection === 'overview') {
              return <ProjectOverview project={project} onRefresh={refreshProjects} />;
            } else if (subsection === 'integrations') {
              return <ProjectIntegrations project={project} />;
            } else if (subsection === 'users') {
              return <ProjectUsers project={project} />;
            }
            return null;
          })()}
        </div>
      </div>

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Create New Project
            </h3>

            <form onSubmit={handleCreateProject} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Project Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={3}
                  placeholder="Brief description of this project..."
                />
              </div>

              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-sm text-blue-800 dark:text-blue-300">
                💡 <strong>Tip:</strong> After creating the project, configure GitHub and Jira integrations in the project's Integrations tab.
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                >
                  Create Project
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({ name: '', description: '' });
                  }}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsNew;
